import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';
import { generateTestId } from '@vs/functions/generate-test-id';
import { PortalUiSrOnlyInputRequired } from '@vs/ui/portal/sr-only-input-required';
import classNames from 'classnames';
import { useId, useRef } from 'react';

import styles from './portal-select.module.css';

export type Option = {
  label: string;
  value: string;
  disable?: boolean;
};

export enum SelectSize {
  sm = 'sm',
  md = 'md',
}

export enum SelectStyle {
  default = 'default',
  bordered = 'bordered',
  ghost = 'ghost',
  primary = 'primary',
  secondary = 'secondary',
  accent = 'accent',
  info = 'info',
  success = 'success',
  warning = 'warning',
}
type RestMuiSelectProps = Omit<
  SelectProps,
  'value' | 'placeholder' | 'label' | 'onChange' | 'size' | 'variant'
>;
export type PortalSelectProps<T extends Option> = {
  options: T[];
  value?: string;
  styleVariant?: keyof typeof SelectStyle;
  placeholder?: string;
  label?: string;
  errorMessage?: string;
  helperText?: string;
  onChange?: (value: T) => void;
  noOptionsText?: string;
  size?: keyof typeof SelectSize;
  paperClassName?: string;
  'data-testid'?: string;
} & RestMuiSelectProps;

interface VsSelectDisplayProps extends React.HTMLAttributes<HTMLDivElement> {
  'data-testid'?: string;
}

export function PortalSelect<T extends Option>({
  styleVariant = 'bordered',
  placeholder = '',
  options = [],
  label,
  value,
  disabled = false,
  required = false,
  error = false,
  errorMessage,
  noOptionsText = '',
  helperText,
  onChange = () => {},
  className = '',
  size,
  'data-testid': dataTestId,
  paperClassName = '',
  id,
  labelId,
  name,
  ...restMuiProps
}: PortalSelectProps<T>) {
  const randomId = useId();
  const _id = id || randomId;
  const randomLabelId = useId();
  const _labelId = labelId || randomLabelId;
  const selectRef = useRef<HTMLElement>(null);

  const classesStyle = classNames({
    [styles['overwrite-default']]: styleVariant === SelectStyle.default,
    [styles['overwrite-bordered']]: styleVariant === SelectStyle.bordered,
    [styles['overwrite-ghost']]: styleVariant === SelectStyle.ghost,
    [styles['overwrite-primary']]: styleVariant === SelectStyle.primary,
    [styles['overwrite-secondary']]: styleVariant === SelectStyle.secondary,
    [styles['overwrite-info']]: styleVariant === SelectStyle.info,
    [styles['overwrite-accent']]: styleVariant === SelectStyle.accent,
    [styles['overwrite-success']]: styleVariant === SelectStyle.success,
    [styles['overwrite-warning']]: styleVariant === SelectStyle.warning,
  });

  const classesBase = classNames(
    styles['overwrite'],
    size === undefined
      ? styles['size-auto']
      : size === 'sm'
        ? styles['size-sm']
        : size === 'md'
          ? styles['size-md']
          : ''
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      selectRef.current?.focus();
    }
  };

  return (
    <div
      className={classNames(
        classesBase,
        styles['select-container'],
        styles['focusable'],
        'p-1',
        {
          [styles['overwrite-disabled']]: disabled,
          [styles['overwrite-error']]: error,
          [classesStyle]: !disabled && !error,
        },
        className
      )}
      {...generateTestId('form-control', [name])}
    >
      {Boolean(label) && (
        <InputLabel variant="standard" id={_labelId} htmlFor={_id}>
          {label}
          <span
            className={classNames(
              'text-vs-error-focus',
              required ? 'visible' : 'invisible'
            )}
          >
            *<PortalUiSrOnlyInputRequired />
          </span>
        </InputLabel>
      )}
      <Select
        {...restMuiProps}
        value={value ?? ''}
        data-testid={dataTestId}
        SelectDisplayProps={
          {
            ...generateTestId('select', [name]),
          } as VsSelectDisplayProps
        }
        inputRef={selectRef}
        inputProps={{
          id: _id,
          labelId: _labelId,
          onKeyDown: handleKeyDown,
          ...generateTestId('input', [name]),
        }}
        name={name}
        onChange={event => {
          onChange(options.find(o => o.value === event.target.value) as T);
        }}
        disabled={disabled}
        error={error}
        IconComponent={props => {
          return props.className.includes('MuiSelect-iconOpen') ? (
            <ExpandLessIcon {...props} />
          ) : (
            <ExpandMoreIcon {...props} />
          );
        }}
        displayEmpty={Boolean(placeholder)}
        renderValue={selected =>
          options.find(option => option.value === selected)?.label || (
            <span>{placeholder}</span>
          )
        }
        autoFocus={false}
        MenuProps={{
          slotProps: {
            paper: {
              className: classNames(styles['overwrite-paper'], paperClassName),
              ...generateTestId('menu', [name]),
            },
          },
        }}
      >
        {Boolean(noOptionsText) && (
          <MenuItem disabled value="default">
            {noOptionsText}
          </MenuItem>
        )}
        {options.map((option, index) => (
          <MenuItem
            {...(dataTestId
              ? generateTestId({ id: `${dataTestId}-item-${index}` })
              : generateTestId('option', [name, index]))}
            key={index}
            value={option.value}
            disabled={option.disable}
            className="vs-body-xs-regular sm:vs-body-sm-regular whitespace-pre-wrap mb-2"
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {(errorMessage || helperText) && (
        <FormHelperText
          className={classNames(
            error ? 'text-vs-error-focus' : 'text-vs-base-content-variant'
          )}
          aria-live="polite"
        >
          {error ? errorMessage : helperText}
        </FormHelperText>
      )}
    </div>
  );
}

export default PortalSelect;
